/**
 * Mission modifiers (04 §7), intel cards (04 §8) and the sky over each biome. All data.
 *
 * `excludes` is written out by hand, and tests/unit/meta3Modifiers.test.ts checks it against the FACTS table
 * (archetypeFacts.ts): no Night Op where the job needs daylight, no Scattered Drop where a pod carries the
 * mission or there is no drop at all, nothing that plays on Recall where Recall is disabled, and no garrison
 * already on alert where the first alert loses the mission with no countdown.
 */

import type { ArchetypeId, Biome, IntelId, ModifierId, TimeOfDay, WeatherKind } from '../../sim/types';
import { DOG_TEAMS } from './dogs';

export interface ModifierInfo {
  id: ModifierId;
  name: string;
  text: string;
  /** Reward multiplier for flying with it. */
  reward: number;
  /** Modifiers it may never be combined with. */
  conflicts: ModifierId[];
  /** Archetypes that already do this, or that it would break. */
  excludes: ArchetypeId[];
  /** Only dealt to a mission rated this or higher (`modifierAllowed`'s `threat`). */
  minThreat?: number;
}

export const MODIFIERS: readonly ModifierInfo[] = [
  // R6: the figure is the sim's. `ENEMY.range` falls to 10 m at night from the 20 m a briefing can
  // otherwise expect (`TIME_WEIGHTS` over dawn/day/dusk) - a half, not the two fifths it claimed.
  // "yours -25 %" is gone with it: the player's night is a colour grade (render/pipeline/options.ts),
  // it has no range to cut, and a number nothing can be measured against is the defect of this round.
  { id: 'night-op', name: 'Night Op', reward: 1.2, conflicts: [], excludes: ['photograph-convoy'],
    text: 'Night: enemy vision -50 %; searchlights are lit.' },
  // R5: the numbers are now the sim's (src/sim/weather.ts). Until round five nothing read them at all.
  // R5 (thunderstorm): it named a "downpour" on temperate ground and brought the rain a calm day
  // brings - no heavier wind than a wet Tuesday for a 1.25 reward. Each of the three is a weather of
  // its own now, and all three claims hold for every one of them: `stormVision` halves sight, `WIND`
  // gives all three the same heavy band, `stormFatigue` charges the fifth. The stroke stays OFF this
  // line on purpose - one card covers three skies, and a blizzard throws no lightning, so promising
  // it here would tell an arctic player about a thing he will never see.
  { id: 'storm', name: 'Storm', reward: 1.25, conflicts: ['scattered-drop'], excludes: [],
    text: 'Whiteout, sandstorm or thunderstorm: enemy vision -50 %, heavy wind, stamina drain +20 %.' },
  { id: 'high-alert', name: 'High Alert', reward: 1.3, conflicts: [],
    excludes: ['code-book', 'courier-ambush', 'defector'],
    text: 'The garrison starts at alert level 1 and patrols are doubled.' },
  { id: 'elite-garrison', name: 'Elite Garrison', reward: 1.3, conflicts: [], excludes: ['code-book', 'radar-array'],
    text: 'Enemy soldiers are shifted one tier up.' },
  { id: 'scattered-drop', name: 'Scattered Drop', reward: 1.15, conflicts: ['storm'],
    excludes: ['partisan-rendezvous', 'beacon', 'prison-break'],
    text: 'Strong variable wind: pods drift far, each its own way, and may land badly.' },
  { id: 'short-fuse', name: 'Short Fuse', reward: 1.25, conflicts: [], excludes: ['diversion', 'hold-crossing'],
    text: 'Mission clock -25 %.' },
  { id: 'two-pod-limit', name: 'Two-Pod Limit', reward: 1.15, conflicts: [], excludes: [],
    text: 'One fewer supply pod.' },
  // R6: "twice" holds - `MINES.denseFactor` sows each patch half again as thickly and
  // `BUDGET.fieldworksDense` buys the extra patches, which together measure 1.7x at threat 3 and
  // 3.0x at threat 9. "Some outside the marked fields" did not: every mine the sim lays is inside a
  // declared rectangle (`createHazardState`), there is no stray-mine path, and there never was one.
  { id: 'dense-minefields', name: 'Dense Minefields', reward: 1.2, conflicts: [], excludes: [],
    text: 'Twice the mines: more fields, and each of them sown thicker.' },
  { id: 'hot-extraction', name: 'Hot Extraction', reward: 1.2, conflicts: [], excludes: ['diversion', 'hold-crossing'],
    text: 'When the aircraft is inbound the alarm goes up, a squad converges on the pickup, and it holds for less long.' },
  { id: 'radio-silence', name: 'Radio Silence', reward: 1.2, conflicts: [], excludes: [],
    text: 'No strip map and no markers on the HUD: study the ground on the pass over the target.' },
  // ROUND8: one team on top of any the threat brings (src/meta/dogs.ts), bought outside the garrison budget
  // (src/gen/dogTeams.ts), so the card adds a team rather than trading one for riflemen. Every claim is the
  // sim's contract (ROUND8-contracts.md §1): the dog runs the scent he left, wading wipes it, it bays and never
  // bites. Nothing is excluded - a bark is a shout, never the alarm a quiet mission is lost by.
  { id: 'dog-patrols', name: 'Dog Patrols', reward: 1.2, conflicts: [], excludes: [],
    minThreat: DOG_TEAMS.modifierFrom,
    text: 'An extra dog team works the approaches: the dog follows your trail and barks its handler on to you.'
      + ' Wade through water to break the trail.' },
];

export interface IntelInfo {
  id: IntelId;
  name: string;
  text: string;
  /** RP at the briefing. */
  price: number;
  /** Missions the card can do nothing for: it is never offered at their briefing. */
  excludes?: readonly ArchetypeId[];
  /**
   * All this card buys is ink on the strip map, so Radio Silence - which takes the strip map away -
   * would leave it worth nothing at all. Such a card is never offered on a silent mission (`intelFor`).
   * Drawing it anyway was the other way out and is worse: the modifier's whole point is that the map
   * is gone, and a card that survived it would sell its way round a condition the player was paid for.
   */
  needsMap?: true;
}

export const INTEL: readonly IntelInfo[] = [
  { id: 'recon-photos', name: 'Recon Photos', price: 5, text: "Reveals the map's structures.", needsMap: true },
  { id: 'patrol-schedule', name: 'Patrol Schedule', price: 8, text: 'Patrol routes are drawn on the map.',
    needsMap: true },
  // R6 P9 (handed over by SKILLS): the card is now only offered from `SKETCH_MIN_THREAT`, where the
  // generator starts leaving fields unmarked - so what it buys is the HIDDEN ones, and saying only
  // "minefields are marked" left it looking like 6 RP for something a low-threat map gives free.
  { id: 'sappers-sketch', name: "Sapper's Sketch", price: 6,
    text: 'Every minefield is marked, including the ones the garrison hid.' },
  { id: 'weather-window', name: 'Weather Window', price: 4, text: 'Removes the storm from this mission.' },
  { id: 'inside-man', name: 'Inside Man', price: 10, text: 'One gate stands unlocked and one guard is absent.' },
  // Prison Break has no drop: its pods already lie where they fell, and nobody can add one.
  { id: 'pathfinder-team', name: 'Pathfinder Team', price: 9, text: 'A fourth pod is pre-placed at a marked spot.',
    excludes: ['prison-break'] },
  { id: 'alternate-lz', name: 'Alternate LZ', price: 7, text: 'A second extraction point.' },
  { id: 'jammed-radios', name: 'Jammed Radios', price: 8, text: 'Reinforcements are delayed by 60 s.' },
];

/**
 * Threat from which a generated minefield may be left unmarked, and so the threat from which
 * Sapper's Sketch has anything to uncover. Written out here, as `excludes` is, because `src/meta`
 * does not reach into the generator; tests/unit/round6Content.test.ts checks it against
 * `MINES.hiddenFrom`, which is the fact it mirrors (R6).
 */
export const SKETCH_MIN_THREAT = 6;

/** The cards that can be of use on a mission of this archetype, under these conditions: what its briefing may offer. */
export function intelFor(
  id: ArchetypeId, modifiers: readonly ModifierId[] = [], threat: number = SKETCH_MIN_THREAT,
): readonly IntelInfo[] {
  const silent = modifiers.includes('radio-silence');
  // R6: below `SKETCH_MIN_THREAT` the generator marks every field it lays, so the Sketch buys the
  // player nothing at all. A card that cannot act is not put up for sale - the same rule that keeps
  // the map cards off a Radio Silence briefing.
  const idle = (card: IntelInfo): boolean => card.id === 'sappers-sketch' && threat < SKETCH_MIN_THREAT;
  return INTEL.filter((card) =>
    !(card.excludes ?? []).includes(id) && !(silent && card.needsMap === true) && !idle(card));
}

/** Cards offered at each briefing. */
export const INTEL_OFFERS = 3;

export const MODIFIER_RULES = {
  /** Chance of 0, 1 or 2 modifiers on a node, by theatre (later theatres are dressed harder). */
  countWeights: [[55, 35, 10], [40, 40, 20], [25, 45, 30]] as ReadonlyArray<readonly number[]>,
  /** Non-native pairing (03 §2.1). */
  variantReward: 1.1,
  /** Readiness at which a node may pick up one more, forced, modifier (04 §2.3). */
  forcedAtReadiness: 6,
  forcedChance: 0.35,
} as const;

/** Relative weights of dawn / day / dusk; night comes only with the Night Op modifier. */
export const TIME_WEIGHTS: ReadonlyArray<readonly [TimeOfDay, number]> = [['dawn', 20], ['day', 60], ['dusk', 20]];

/**
 * Ordinary weather by biome, and what the Storm modifier turns it into.
 *
 * ROUND5: temperate's storm was `rain` - the same rain a calm day rolls 35 % of the time - so the
 * card bought a mission nothing distinguished from an ordinary wet one, and the Practice picker had
 * no storm to offer a temperate map. Every `storm` entry here is now a weather that is a storm on
 * its own (tests/unit/thunderstorm.test.ts checks that against `inStorm`), which is what makes the
 * card's promise true by construction rather than by the card's own say-so.
 */
export const WEATHER: Record<Biome, { calm: ReadonlyArray<readonly [WeatherKind, number]>; storm: WeatherKind }> = {
  arctic: { calm: [['clear', 55], ['snow', 45]], storm: 'blizzard' },
  desert: { calm: [['clear', 100]], storm: 'sandstorm' },
  temperate: { calm: [['clear', 65], ['rain', 35]], storm: 'thunderstorm' },
};
